import React from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'react-toastify';
import MagicButton from 'components/MagicButton/MagicButton';
import {
  AdminAffiliateEarning,
  useMarkEarningsPaidMutation,
} from 'store/services/affiliates/affiliatesApi';
import { formatCents, getApiErrorMessage } from './affiliateAdminUtils';

interface MarkPaidModalProps {
  isOpen: boolean;
  selectedRows: AdminAffiliateEarning[];
  onClose: () => void;
  /** Called after a successful mark-paid so the parent can clear selection. */
  onSuccess: () => void;
}

interface PerAffiliateGroup {
  paypalEmail: string;
  count: number;
  subtotalCents: number;
}

const groupByPaypalEmail = (rows: AdminAffiliateEarning[]): PerAffiliateGroup[] => {
  const groups = new Map<string, PerAffiliateGroup>();
  rows.forEach((row) => {
    const key = row.paypalEmail ?? `(no PayPal email) — ${row.affiliateUsername}`;
    const existing = groups.get(key);
    if (existing) {
      existing.count += 1;
      existing.subtotalCents += row.amountCents;
    } else {
      groups.set(key, { paypalEmail: key, count: 1, subtotalCents: row.amountCents });
    }
  });
  return Array.from(groups.values());
};

const MarkPaidModal: React.FC<MarkPaidModalProps> = ({
  isOpen,
  selectedRows,
  onClose,
  onSuccess,
}) => {
  const [markEarningsPaid, { isLoading }] = useMarkEarningsPaidMutation();

  if (!isOpen) return null;

  const totalCents = selectedRows.reduce((sum, row) => sum + row.amountCents, 0);
  const perAffiliate = groupByPaypalEmail(selectedRows);

  const handleConfirm = async () => {
    if (selectedRows.length === 0 || isLoading) return;
    try {
      const result = await markEarningsPaid({
        earningIds: selectedRows.map((row) => row.id),
      }).unwrap();
      toast.success(
        `Marked ${result.updatedCount} earning${result.updatedCount === 1 ? '' : 's'} as paid (payout batch ${result.payoutBatchId})`
      );
      onSuccess();
      onClose();
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Failed to mark earnings as paid. Please try again.'));
    }
  };

  const modalContent = (
    <div className="admin-affil-modal-overlay" onClick={onClose}>
      <div className="admin-affil-modal" onClick={(e) => e.stopPropagation()}>
        <div className="admin-affil-modal-header">
          <h2 className="admin-affil-modal-title">Mark Earnings as Paid</h2>
          <button className="admin-affil-modal-close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        <div className="admin-affil-modal-content">
          <p className="admin-affil-modal-text">
            You are about to mark{' '}
            <strong>
              {selectedRows.length} earning{selectedRows.length === 1 ? '' : 's'}
            </strong>{' '}
            totaling <strong>{formatCents(totalCents)}</strong> as paid.
          </p>

          <div className="admin-affil-modal-section">
            <h3 className="admin-affil-modal-subtitle">Per-Affiliate Payout Summary</h3>
            <div className="admin-affil-payout-list">
              {perAffiliate.map((group) => (
                <div key={group.paypalEmail} className="admin-affil-payout-row">
                  <span className="admin-affil-payout-email">{group.paypalEmail}</span>
                  <span className="admin-affil-payout-count">
                    {group.count} earning{group.count === 1 ? '' : 's'}
                  </span>
                  <span className="admin-affil-payout-subtotal">
                    {formatCents(group.subtotalCents)}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <p className="admin-affil-modal-hint">
            This action assigns a payout batch ID and cannot be undone from this page.
          </p>
        </div>

        <div className="admin-affil-modal-footer">
          <MagicButton label="Cancel" color="grey" onPress={onClose} isDisabled={isLoading} />
          <MagicButton
            label={isLoading ? 'Marking Paid...' : 'Confirm Mark as Paid'}
            color="purple"
            onPress={handleConfirm}
            isLoading={isLoading}
            isDisabled={selectedRows.length === 0 || isLoading}
          />
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default MarkPaidModal;
